<div class="w-full h-full overflow-hidden grid grid-cols-2 gap-4">
    <article
        class="col-span-1 divide-y divide-zinc-200 dark:divide-zinc-600 overflow-hidden rounded-lg bg-white dark:bg-gray-800 card-container-pale flex flex-col">
        <div class="px-2 pb-3 shrink-0">
            <div class="flex items-center gap-2">
                <h3 class="text-sm font-semibold text-primary">Compliance Overview</h3>
            </div>

            <p class="mt-1 text-xs text-secondary">How accurately did the creators achieve the main brief points?</p>
        </div>

        <div class="px-2 py-3 flex flex-col gap-y-4 overflow-y-auto scrollbar-transparent flex-1 min-h-0">
            @if (trendsLoadingState() === 'loading' || trendsLoadingState() === 'idle') {
            <div class="flex flex-1 items-center justify-center py-8">
                <atom-loading-inline title="Loading" message="Curating accuracy report."></atom-loading-inline>
            </div>
            } @else if (trendsLoadingState() === 'error') {
            <div class="flex flex-col items-center justify-center py-8 text-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"
                    class="text-gray-400 dark:text-gray-500 mb-2" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" x2="12" y1="8" y2="12" />
                    <line x1="12" x2="12.01" y1="16" y2="16" />
                </svg>
                <h3 class="text-sm font-semibold text-gray-900 dark:text-white">Unable to load compliance data</h3>
                <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Please try refreshing the page.</p>
            </div>
            } @else if (noodleQuestionTrends()!.trends.length > 0) {
            <!-- Summary Card -->
            <div
                class="border-l-4 border-primary dark:border-primary bg-primary/10 dark:bg-primary/15 p-4 rounded-r-lg">
                <div class="flex items-center justify-between">
                    <div class="flex items-center gap-3">
                        <svg viewBox="0 0 20 20" fill="currentColor" data-slot="icon" aria-hidden="true"
                            class="size-5 text-accent">
                            <path
                                d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495ZM10 5a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 10 5Zm0 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"
                                clip-rule="evenodd" fill-rule="evenodd" />
                        </svg>
                        <p class="text-sm text-accent/90">
                            Unique content items analyzed
                        </p>
                    </div>
                    <p class="font-medium text-accent/90">{{
                        formatService.convertIntToString(noodleQuestionTrends()!.uniqueContentCount) }}</p>
                </div>
            </div>

            <!-- Circular Overall Metrics -->
            <div class="flex flex-col items-center justify-center py-4">
                <div class="relative w-48 h-48">
                    <!-- SVG Circle -->
                    <svg class="transform -rotate-90 w-full h-full" viewBox="0 0 100 100">
                        <!-- Background Circle -->
                        <circle cx="50" cy="50" r="45" fill="none" stroke="currentColor" stroke-width="8"
                            class="text-gray-200 dark:text-gray-700" />

                        <!-- Approval Arc (Green) -->
                        @if (getAggregatedApprovalPercentage() > 0) {
                        <circle cx="50" cy="50" r="45" fill="none" stroke="currentColor" stroke-width="8"
                            stroke-linecap="round" [attr.stroke-dasharray]="getCircleCircumference()"
                            [attr.stroke-dashoffset]="getApprovalDashOffset()"
                            class="text-green-500 dark:text-green-600 transition-all duration-500" />
                        }

                        <!-- Issue Arc (Red) -->
                        @if (getAggregatedIssuePercentage() > 0) {
                        <circle cx="50" cy="50" r="45" fill="none" stroke="currentColor" stroke-width="8"
                            stroke-linecap="round" [attr.stroke-dasharray]="getIssueDashArray()"
                            [attr.stroke-dashoffset]="getIssueDashOffset()"
                            class="text-red-500 dark:text-red-600 transition-all duration-500" />
                        }
                    </svg>

                    <!-- Center Content -->
                    <div class="absolute inset-0 flex flex-col items-center justify-center">
                        <div class="text-center">
                            <p class="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Overall Compliance</p>
                            <p class="text-2xl font-bold text-accent">
                                {{ getAggregatedApprovalPercentage() | number: '1.0-0' }}%
                            </p>
                            <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {{ formatService.convertIntToString(getTotalApproved()) }} / {{
                                formatService.convertIntToString(getTotalReviews()) }}
                            </p>
                        </div>
                    </div>
                </div>

                <!-- Legend -->
                <div class="flex flex-row items-center gap-x-6 mt-4">
                    <div class="flex flex-row items-center gap-x-2">
                        <div class="w-3 h-3 rounded-full bg-green-500 dark:bg-green-600"></div>
                        <span class="text-xs text-gray-600 dark:text-gray-400">Approved</span>
                    </div>
                    <div class="flex flex-row items-center gap-x-2">
                        <div class="w-3 h-3 rounded-full bg-red-500 dark:bg-red-600"></div>
                        <span class="text-xs text-gray-600 dark:text-gray-400">Issues</span>
                    </div>
                </div>
            </div>

            <!-- Trends List -->
            @for (trend of noodleQuestionTrends()!.trends; track trend.briefId) {
            <div class="flex flex-col w-full items-start bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 gap-y-3">
                <!-- Statement -->
                <p class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ trend.statement }}</p>

                <!-- Metrics Grid -->
                <div class="flex flex-col gap-y-3 w-full">
                    <!-- Approval Rate -->
                    <div class="flex flex-col gap-y-1.5">
                        <div class="flex flex-row items-center justify-between">
                            <span class="text-xs font-medium text-gray-600 dark:text-gray-400">Approval Rate</span>
                            <span class="text-xs font-semibold text-green-700 dark:text-green-300">
                                {{ trend.approvedPercentage | number: '1.1-1' }}%
                            </span>
                        </div>
                        <div class="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                            <div class="bg-green-500 dark:bg-green-600 h-2 rounded-full transition-all duration-300"
                                [style.width.%]="trend.approvedPercentage"></div>
                        </div>
                        <div class="flex flex-row items-center gap-x-2 text-xs text-gray-500 dark:text-gray-400">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round" class="lucide lucide-check-circle-2">
                                <circle cx="12" cy="12" r="10" />
                                <path d="m9 12 2 2 4-4" />
                            </svg>
                            <span>{{ formatService.convertIntToString(trend.approvedCount) }} approved out of {{
                                formatService.convertIntToString(trend.totalReviews) }} reviews</span>
                        </div>
                    </div>

                    <!-- Issue Found Rate -->
                    <div class="flex flex-col gap-y-1.5">
                        <div class="flex flex-row items-center justify-between">
                            <span class="text-xs font-medium text-gray-600 dark:text-gray-400">Issues Found</span>
                            <span class="text-xs font-semibold text-red-700 dark:text-red-300">
                                {{ trend.issueFoundPercentage | number: '1.1-1' }}%
                            </span>
                        </div>
                        <div class="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                            <div class="bg-red-500 dark:bg-red-600 h-2 rounded-full transition-all duration-300"
                                [style.width.%]="trend.issueFoundPercentage"></div>
                        </div>
                        <div class="flex flex-row items-center gap-x-2 text-xs text-gray-500 dark:text-gray-400">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round" class="lucide lucide-alert-circle">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" x2="12" y1="8" y2="12" />
                                <line x1="12" x2="12.01" y1="16" y2="16" />
                            </svg>
                            <span>{{ formatService.convertIntToString(trend.issueFoundCount) }} issues found</span>
                        </div>
                    </div>
                </div>
            </div>
            }
            } @else {
            <div class="flex-1 flex flex-col items-center justify-center py-8 text-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"
                    class="text-gray-400 dark:text-gray-500 mb-2" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" x2="12" y1="8" y2="12" />
                    <line x1="12" x2="12.01" y1="16" y2="16" />
                </svg>
                <p class="text-sm text-gray-500 dark:text-gray-400">No trend data available</p>
            </div>
            }
        </div>
    </article>

    <article
        class="col-span-1 divide-y divide-zinc-200 dark:divide-zinc-600 overflow-hidden rounded-lg bg-white dark:bg-gray-800 card-container-pale flex flex-col">
        <div class="px-2 pb-3 shrink-0">
            <div class="flex items-center gap-2">
                <h3 class="text-sm font-semibold text-primary">Approved Video Accuracy</h3>
            </div>

            <p class="mt-1 text-xs text-secondary">How accurate were the approved videos?</p>
        </div>

        <div class="px-2 py-3 flex flex-col gap-y-4 overflow-y-auto scrollbar-transparent flex-1 min-h-0">
            @if (approvedVideosLoadingState() === 'loading' || approvedVideosLoadingState() === 'idle') {
            <div class="flex flex-1 items-center justify-center py-8">
                <atom-loading-inline title="Loading" message="Curating accuracy report."></atom-loading-inline>
            </div>
            } @else if (approvedVideosLoadingState() === 'error') {
            <div class="flex-1 flex flex-col items-center justify-center py-8 text-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"
                    class="text-gray-400 dark:text-gray-500 mb-2" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" x2="12" y1="8" y2="12" />
                    <line x1="12" x2="12.01" y1="16" y2="16" />
                </svg>
                <h3 class="text-sm font-semibold text-gray-900 dark:text-white">Unable to load approval data</h3>
                <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Please try refreshing the page.</p>
            </div>
            } @else if (approvedModeratedVideos()!.length > 0) {
            <!-- Approval Rate Summary -->
            <div class="flex flex-col items-center justify-center py-4">
                <div class="mx-auto flex max-w-xs flex-col gap-y-0">
                    <dt class="text-base/7 text-primary text-center">Overall Approval Rate</dt>
                    <dd class="order-first text-3xl font-semibold tracking-tight text-accent text-center">
                        {{ approvalRate() }}%
                    </dd>
                </div>
                <p class="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
                    {{ formatService.convertIntToString(getTotalPassedVideos()) }} passed out of {{
                    formatService.convertIntToString(getTotalApprovedVideos()) }} reviews
                </p>
            </div>

            <!-- Most Failed Questions -->
            @if (mostFailedQuestions() && mostFailedQuestions()!.length > 0) {
            <div
                class="border-l-4 border-orange-400 dark:border-orange-500 bg-orange-50 dark:bg-orange-900/30 p-4 rounded-r-lg">
                <div class="flex items-center gap-3 mb-2">
                    <svg viewBox="0 0 20 20" fill="currentColor" data-slot="icon" aria-hidden="true"
                        class="size-5 text-orange-400 dark:text-orange-400">
                        <path
                            d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495ZM10 5a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 10 5Zm0 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"
                            clip-rule="evenodd" fill-rule="evenodd" />
                    </svg>
                    <p class="text-sm font-medium text-orange-700 dark:text-orange-300">
                        Areas Needing Attention
                    </p>
                </div>
                <p class="text-xs text-orange-600 dark:text-orange-400">
                    Questions with the highest failure rates
                </p>
            </div>

            @for (question of mostFailedQuestions(); track question.id) {
            <div class="flex flex-col w-full items-start bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 gap-y-3">
                <!-- Question Statement -->
                <div class="flex flex-row items-start justify-between w-full gap-x-2">
                    <p class="text-sm font-medium text-gray-900 dark:text-gray-100 flex-1">{{ question.question }}</p>
                    <span
                        class="inline-flex items-center rounded-md bg-red-50 dark:bg-red-900/30 px-2 py-1 text-xs font-medium text-red-700 dark:text-red-300 ring-1 ring-inset ring-red-600/20 dark:ring-red-500/30 shrink-0">
                        {{ question.percentage }}% failed
                    </span>
                </div>

                <!-- Failure Rate Progress -->
                <div class="flex flex-col gap-y-1.5 w-full">
                    <div class="flex flex-row items-center justify-between">
                        <span class="text-xs font-medium text-gray-600 dark:text-gray-400">Failure Rate</span>
                        <span class="text-xs font-semibold text-red-700 dark:text-red-300">
                            {{ question.percentage }}%
                        </span>
                    </div>
                    <div class="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                        <div class="bg-red-500 dark:bg-red-600 h-2 rounded-full transition-all duration-300"
                            [style.width.%]="question.percentage"></div>
                    </div>
                    <div class="flex flex-row items-center gap-x-2 text-xs text-gray-500 dark:text-gray-400">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                            class="lucide lucide-x-circle">
                            <circle cx="12" cy="12" r="10" />
                            <path d="m15 9-6 6" />
                            <path d="m9 9 6 6" />
                        </svg>
                        <span>{{ formatService.convertIntToString(question.failed) }} failed out of {{
                            formatService.convertIntToString(question.passed + question.failed) }} reviews</span>
                    </div>
                </div>

                <!-- Type Badge -->
                @if (question.type) {
                <div class="flex flex-row items-center gap-x-2">
                    <span class="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium"
                        [class]="question.type === 'do'
                          ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                          : question.type === 'dont'
                            ? 'bg-primary/10 text-accent dark:bg-primary/25'
                            : ''">
                        @if (question.type === 'do') {
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                            class="mr-1">
                            <path d="M5 13l4 4L19 7" />
                        </svg>
                        Do
                        } @else {
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                            class="mr-1">
                            <path d="M18 6L6 18" />
                            <path d="M6 6l12 12" />
                        </svg>
                        Don't
                        }
                    </span>
                </div>
                }
            </div>
            }
            } @else {
            <div class="flex flex-col items-center justify-center py-8 text-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"
                    class="text-gray-400 dark:text-gray-500 mb-2">
                    <path d="M9 12l2 2 4-4" />
                    <path d="M21 12c.552 0 1-.448 1-1s-.448-1-1-1-1 .448-1 1 .448 1 1 1z" />
                    <path d="M3 12c.552 0 1-.448 1-1s-.448-1-1-1-1 .448-1 1 .448 1 1 1z" />
                    <path d="M12 21c.552 0 1-.448 1-1s-.448-1-1-1-1 .448-1 1 .448 1 1 1z" />
                    <path d="M12 3c.552 0 1-.448 1-1s-.448-1-1-1-1 .448-1 1 .448 1 1 1z" />
                    <circle cx="12" cy="12" r="3" />
                </svg>
                <p class="text-sm text-gray-500 dark:text-gray-400">All questions passed successfully</p>
            </div>
            }
            } @else {
            <div class="flex-1 flex flex-col items-center justify-center py-8 text-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"
                    class="text-gray-400 dark:text-gray-500 mb-2" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" x2="12" y1="8" y2="12" />
                    <line x1="12" x2="12.01" y1="16" y2="16" />
                </svg>
                <p class="text-sm text-gray-500 dark:text-gray-400">No approval data available</p>
            </div>
            }
        </div>
    </article>
</div>